import {
  getReportTemplateDataRowDemographicId, type ReportDemographicData,
} from '~/exportReport/exportReportData.helpers';
import {
  getExportReportFormatType, getReportTableColumnFormulaValue, prepareExportReportFormulaVariables,
} from '~/exportReport/formula/exportReportFormula.helpers';
import { ExportReportTableComponent } from '~/exportReport/table/exportReportTable.component';
import { ExportReportTableDataRowComponent } from '~/exportReport/table/exportReportTableDataRow.component';
import { ExportReportTableEmptyRowComponent } from '~/exportReport/table/exportReportTableEmptyRow.component';
import { ExportReportTableHeaderComponent } from '~/exportReport/table/exportReportTableHeader.component';
import { ExportReportTableTitleRowComponent } from '~/exportReport/table/exportReportTableTitleRow.component';
import type {
  ExportReportTableValue, ReportTableColumn,
} from '~/exportReport/table/ReportTable.model';
import type { ReportTemplateTable } from '~/exportReport/template/reportTemplate';
import {
  applyConditionalStyleToReportTableRowValues, getDemographicTableCellValue,
} from '~/exportReport/templateRenderer/templateRenderer.helpers';

type ExportReportTemplateTableRendererProps = Readonly<{
  className?: string;
  tableColumns: ReadonlyArray<ReportTableColumn>;
  templateTable: ReportTemplateTable;
  currentDataSource?: ReportTableColumn;
  onTableRowClick?: ((itemIndex: number, rowIndex: number) => void);
  itemIndex: number;
  dataSourceColumns: ReadonlyArray<ReportTableColumn>;
  isPreviewMode?: boolean;
  isLoading?: boolean;
  getDemographicData: (columnId: string, demographicId: string) => ReportDemographicData | null;
  getDemographicLabel: (demographicId: string) => string | null;
}>;

export const ExportReportTemplateTableRendererComponent = ({
  className, tableColumns, templateTable, onTableRowClick, itemIndex, dataSourceColumns, isPreviewMode,
  isLoading, getDemographicData, getDemographicLabel, currentDataSource,
}: ExportReportTemplateTableRendererProps) => {
  return (
    <ExportReportTableComponent className={className}>
      <ExportReportTableHeaderComponent columnNames={tableColumns} />

      <tbody>
        {templateTable.content.map((templateRow, rowIndex) => {
          const handleTableRowClick = onTableRowClick
            ? () => onTableRowClick(itemIndex, rowIndex)
            : undefined;

          if (templateRow.type === 'data-row') {
            if (templateRow.hidden) {
              return null;
            }

            let values: ExportReportTableValue[];

            if (templateRow.cells && currentDataSource !== undefined) {
              const rowCells = templateRow.cells;
              const columns = templateTable.columns ?? [];

              // calculate raw values for all cells
              const rowValues = rowCells.map((cell, cellIndex) => {
                if (isPreviewMode) {
                  const value = cell.format_type ?? 'N/A';
                  return {
                    value,
                    valueRaw: value,
                  };
                }

                const formula = columns[cellIndex]?.formula;
                const column = columns[cellIndex];

                if (formula) {
                  return getReportTableColumnFormulaValue({
                    formula,
                    formatType: getExportReportFormatType({
                      column, cell, formula,
                    }),
                    variables: prepareExportReportFormulaVariables({
                      columns, cells: rowCells, getDemographicData,
                      dataSourceColumnId: currentDataSource.id,
                    }),
                    unit: cell.unit ?? '',
                  });
                }

                return getDemographicTableCellValue({
                  column: currentDataSource, templateRow: cell, getDemographicData, tableDefaultPercentageCalcMetricId: templateTable.default_percentage_calc_metric,
                });
              });

              values = applyConditionalStyleToReportTableRowValues({
                tableRowValues: rowValues,
                columns,
              });
            }
            else {
              values = dataSourceColumns.map(column => {
                if (isPreviewMode) {
                  const value = templateRow.format_type ?? 'N/A';

                  return {
                    value,
                    valueRaw: value,
                  };
                }

                return getDemographicTableCellValue({
                  column,
                  templateRow,
                  getDemographicData,
                  tableDefaultPercentageCalcMetricId: templateTable.default_percentage_calc_metric,
                });
              });
            }

            const demographicId = getReportTemplateDataRowDemographicId(templateRow);
            const demographicLabel = getDemographicLabel(demographicId);

            const isMissingDemographicLabel = !demographicLabel && !templateRow.custom_label;

            const label = templateRow.custom_label ?? demographicLabel ?? (isMissingDemographicLabel ? demographicId : '');
            const isLabelError = !isLoading && (isMissingDemographicLabel || values.some(value => value.isError));

            return (
              <ExportReportTableDataRowComponent
                style={templateRow.style}
                isInteractive={!!handleTableRowClick}
                isLabelError={isLabelError}
                key={`data-row${rowIndex}`}
                label={label}
                onClick={handleTableRowClick}
                values={values}
              />
            );
          }

          if (templateRow.type === 'title-row') {
            return (
              <ExportReportTableTitleRowComponent
                key={`title-row-${rowIndex}`}
                isInteractive={!!handleTableRowClick}
                onClick={handleTableRowClick}
                style={templateRow.style}
                title={templateRow.content}
              />
            );
          }

          if (templateRow.type === 'empty-row') {
            return (
              <ExportReportTableEmptyRowComponent
                key={`empty-row-${rowIndex}`}
                isInteractive={!!handleTableRowClick}
                onClick={handleTableRowClick}
              />
            );
          }

          return null;
        })}
      </tbody>
    </ExportReportTableComponent>
  );
};
